// Microsoft Entra External ID (CIAM) login via MSAL.js.
// Configured from Vite env vars baked at build time:
//   VITE_B2C_TENANT          atlasgeostudio.onmicrosoft.com
//   VITE_B2C_TENANT_ID       11b94954-300b-4081-9244-d0cb111030d8
//   VITE_B2C_CLIENT_ID       fa7e469d-0d53-4f49-aeb3-fe6dfe062be3
//   VITE_B2C_AUTHORITY_HOST  ciamlogin.com
//   VITE_B2C_POLICY          SignUpSignIn
//
// If any required value is missing, msalEnabled() returns false and the UI
// hides the "Sign in with Microsoft" button — the existing email + access
// key form keeps working as the only path.
import { PublicClientApplication } from '@azure/msal-browser';

const TENANT       = import.meta.env.VITE_B2C_TENANT       || '';
const TENANT_ID    = import.meta.env.VITE_B2C_TENANT_ID    || '';
const CLIENT_ID    = import.meta.env.VITE_B2C_CLIENT_ID    || '';
const HOST         = import.meta.env.VITE_B2C_AUTHORITY_HOST || 'ciamlogin.com';
const POLICY       = import.meta.env.VITE_B2C_POLICY       || 'SignUpSignIn';

export function msalEnabled() {
  return !!(TENANT && CLIENT_ID);
}

let _instance = null;
let _initialized = false;

function authority() {
  const tenantShort = TENANT.split('.')[0];
  if (HOST.toLowerCase() === 'b2clogin.com') {
    // Legacy B2C
    return `https://${tenantShort}.b2clogin.com/${TENANT}/${POLICY}`;
  }
  // Entra External ID (CIAM) — tenant GUID in path, no policy in authority URL
  return `https://${tenantShort}.ciamlogin.com/${TENANT_ID || TENANT}`;
}

function knownAuthorities() {
  const tenantShort = TENANT.split('.')[0];
  return [`${tenantShort}.${HOST}`];
}

export async function getMsal() {
  if (!msalEnabled()) return null;
  if (!_instance) {
    _instance = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: authority(),
        knownAuthorities: knownAuthorities(),
        redirectUri: `${window.location.origin}/auth/callback`,
        postLogoutRedirectUri: `${window.location.origin}/`,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    });
  }
  if (!_initialized) {
    await _instance.initialize();
    _initialized = true;
  }
  return _instance;
}

export async function loginWithMicrosoft() {
  const msal = await getMsal();
  if (!msal) throw new Error('msal_not_configured');
  await msal.loginRedirect({
    scopes: ['openid', 'profile', 'email'],
    prompt: 'select_account',
  });
}

export async function handleRedirectResponse() {
  const msal = await getMsal();
  if (!msal) return null;
  try {
    const result = await msal.handleRedirectPromise();
    return result;  // { account, idToken, ... } or null if not a redirect-return
  } catch (e) {
    console.error('msal_redirect_error', e);
    return null;
  }
}

export async function getIdToken() {
  const msal = await getMsal();
  if (!msal) return null;
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const r = await msal.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email'],
      account: accounts[0],
    });
    return r.idToken;
  } catch {
    return null;
  }
}

export async function msalLogout() {
  const msal = await getMsal();
  if (!msal) return;
  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    await msal.logoutRedirect({ account: accounts[0] });
  }
}
